import React, { useState, useEffect, useRef } from "react";
import "./ApprovedRequestDetails.css";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import strings from "../../../../utils/strings";
import { getRequestSessions, getSessionNurses, assignSessionNurse, getNurseRequestDetails, collectRequestPayment } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import ToastPopup from "../../../../widgets/Toast/toastPopup.js";
import { buildFileUrl, getFileType } from "../../../../utils/fileHelpers";
import { openSecureFileUrl } from "../../../../utils/secureFilePreview";


const formatDate = (dateStr) => {
  if (!dateStr) return "—";

  try {
    const d = new Date(dateStr);

    if (isNaN(d)) return dateStr;

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatTime = (t) => {
  if (!t) return "";
  if (/am|pm/i.test(t)) return t;
  const [h, m] = t.split(":");
  const hr = parseInt(h, 10);
  return `${hr % 12 || 12}:${m || "00"}${hr >= 12 ? "PM" : "AM"}`;
};

const resolveLocalized = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") return val.en || val.vi || Object.values(val).find((v) => typeof v === "string") || "";
  return String(val);
};

const getNurseLabel = (nurse) => {
  if (!nurse) return "";
  if (typeof nurse === "string") return nurse;
  return nurse.label || `${resolveLocalized(nurse.firstName)} ${resolveLocalized(nurse.lastName)}`.trim() || nurse.employeeID || nurse.name || "";
};

const getNurseValue = (nurse) => {
  if (!nurse) return "";
  if (typeof nurse === "string") return nurse;
  return nurse.value || nurse._id || nurse.id || nurse.employeeID || "";
};

const toNurseOption = (nurse) => {
  const value = getNurseValue(nurse);
  const label = getNurseLabel(nurse);
  return value && label ? { value, label } : null;
};

const getSessionNurseOption = (session) => {
  const nurseFields = [session?.nurse, session?.nurses, session?.assignedNurse, session?.assignedNurses, session?.homecareStaff, session?.homecareStaffs];

  for (const field of nurseFields) {
    const nurse = Array.isArray(field) ? field.find(Boolean) : field;
    const option = toNurseOption(nurse);
    if (option) return option;
  }

  const assignedId = [session?.nurseID, session?.nurseId, session?.nurseIDs?.[0], session?.nurseIds?.[0]].find(Boolean);
  return assignedId ? { value: assignedId, label: assignedId } : null;
};

const ApprovedRequestDetails = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const queryParams = new URLSearchParams(location.search);
  const requestId = queryParams.get("id") || "";
  const fromParam = queryParams.get("from") || "";
  const requestListFallback = location.state || {};
  const fromPast = fromParam === "past";
  const fromApproved = fromParam === "approved";
  // Go directly to fullDetails for approved and past tabs; show assign table only for scheduled
  const initialView = fromApproved || fromPast || queryParams.get("view") === "fullDetails" ? "fullDetails" : "assignNurse";

  const backTab = fromPast ? "past" : fromApproved ? "approved" : "approved";
  const breadcrumbs = ["nursingCare", "manageRequests", "serviceDetails"];
  const paths = [strings.navigation.manageNurseReq, `${strings.navigation.manageNurseReq}?activeList=${backTab}`, strings.navigation.viewApprovedRequestDetails];

  const requestSessions = useSelector((state) => state.manageRequest.requestSessions);
  const requestDetails = useSelector((state) => state.manageRequest.requestDetails);
  const approvedRequests = useSelector((state) => state.manageRequest.approvedRequests);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const [viewMode, setViewMode] = useState(initialView);

  // Collect Cash / Waive Off
  const [paymentLoading, setPaymentLoading] = useState({});

  const [paymentModal, setPaymentModal] = useState(null);

  const openPaymentModal = (type) => {
    setPaymentModal(type);
  };

  const handleClosePaymentModal = () => {
    if (paymentLoading[paymentModal]) return;
    setPaymentModal(null);
  };

  const handlePaymentSubmit = (reason) => {
    const type = paymentModal;
    if (!type || paymentLoading[type]) return;

    setPaymentLoading((prev) => ({ ...prev, [type]: true }));

    dispatch(collectRequestPayment({ requestId, type, reason })).then((res) => {
      setPaymentLoading((prev) => ({ ...prev, [type]: false }));
      if (res.meta.requestStatus === "fulfilled") {
        ToastPopup.success(type === "CASH" ? t("cashPaymentRecordedSuccess") : t("waivedOff") || "Waived Off");
        setPaymentModal(null);
        dispatch(getNurseRequestDetails({ id: requestId }));
      } else {
        ToastPopup.error(res.error?.message || t("failedToUpdatePayment"));
      }
    });
  };

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [modalSessionId, setModalSessionId] = useState(null);
  const [pendingNurse, setPendingNurse] = useState(null);

  const [nursesBySession, setNursesBySession] = useState({});
  const [assignedNurses, setAssignedNurses] = useState({}); // { [sessionId]: { value, label } }
  const [staffCounts, setStaffCounts] = useState({});

  const fetchedNursesRef = useRef(new Set());

  // Build requestData from real API response
  const activeRequestDetails = requestDetails?._id === requestId ? requestDetails : null;
  const addr = requestDetails?.address;
  const formatAddress = (a) => (a ? [a.street, a.ward, a.city, a.province, a.postalCode].filter(Boolean).join(", ") : "");
  const approvedRequestFromStore = approvedRequests.find((request) => request._id === requestId);
  const distanceInKm =
    activeRequestDetails?.distanceInKm ?? approvedRequestFromStore?.distanceInKm ?? activeRequestDetails?.zone ?? activeRequestDetails?.address?.zone ?? requestListFallback.distanceInKm ?? "";
  const patientCondition =
    activeRequestDetails?.patientCondition ?? activeRequestDetails?.patient?.patientCondition ?? approvedRequestFromStore?.patientCondition ?? requestListFallback.patientCondition ?? "";

  const getLanguageIcon = (languages = []) => {
    const language = String(languages[0] || "").toLowerCase();
    if (language.includes("viet")) return Icons.vnFlag;
    if (language.includes("fr")) return Icons.frFlag;
    return Icons.enFlag;
  };

  const requestData = {
    id: requestDetails?.nursingRequestID || requestId,
    patientName: requestDetails?.patient?.fullName || `${requestDetails?.patient?.firstName || ""}${requestDetails?.patient?.lastName ? " " + requestDetails.patient.lastName : ""}`.trim() || "",
    gender: requestDetails?.patient?.gender || "",
    dob: requestDetails?.patient?.dateOfBirth || "",
    hn: requestDetails?.patient?.hnNumber || "",
    profilePhoto: requestDetails?.patient?.profilePhoto || "",
    phone: requestDetails?.patient?.countryCode ? `+${requestDetails.patient.countryCode} ${requestDetails.patient.phoneNumber}` : requestDetails?.patient?.phoneNumber || "",
    email: requestDetails?.patient?.email || "",
    insuranceCategory: requestDetails?.patient?.insuranceCategory || "",
    address: formatAddress(addr),
    zone: distanceInKm !== "" && distanceInKm !== null ? `${distanceInKm} km` : "-",
    patientCondition: patientCondition || "-",
    status: requestDetails?.status || "",
    attachments: (requestDetails?.attachments || []).filter(Boolean).map((a) => {
      if (typeof a === "string") {
        const name = a.split("/").pop() || "file";
        return { type: getFileType({ name }), url: buildFileUrl(a), name };
      }
      const rawUrl = a.url || a.path || a.fileUrl || a.filePath || a.location || "";
      const name = a.name || rawUrl.split("/").pop() || "file";
      const type = getFileType({ name, mimeType: a.mimeType || a.mimetype || a.contentType || a.type });
      return { type, url: buildFileUrl(rawUrl), name };
    }),
    description: requestDetails?.description || "",
    nursePreferences: {
      languages: requestDetails?.languagePreferences || [],
      gender: (requestDetails?.genderPreferences || [])[0] || "",
    },
    quotationAmount: requestDetails?.price?.quotation,
    quotationCurrencySymbol: requestDetails?.price?.currencySymbol || "",
    quotation: requestDetails?.quotationURL ? [{ type: "pdf", name: "Quotation", url: requestDetails.quotationURL }] : [],
  };

  useEffect(() => {
    if (requestId) {
      // Fetch request details (patient info, services, preferences etc.)
      dispatch(getNurseRequestDetails({ id: requestId }));
      // Fetch sessions (for the Assign Homecare Staff table — may be empty for VERIFIED requests)
      setSessionsLoading(true);
      dispatch(getRequestSessions({ requestId })).finally(() => setSessionsLoading(false));
    }
  }, [requestId, dispatch]);

  useEffect(() => {
    if (!requestSessions.length) return;

    // Seed assignedNurses from API data so assignments persist across navigation
    const apiAssigned = {};
    requestSessions.forEach((session) => {
      const assigned = getSessionNurseOption(session);
      if (assigned) apiAssigned[session._id] = assigned;
    });
    if (Object.keys(apiAssigned).length) {
      setAssignedNurses((prev) => ({ ...apiAssigned, ...prev }));
    }

    requestSessions.forEach((session) => {
      if (!fetchedNursesRef.current.has(session._id)) {
        fetchedNursesRef.current.add(session._id);
        setNursesBySession((prev) => ({ ...prev, [session._id]: { loading: true, list: [] } }));
        dispatch(getSessionNurses({ sessionId: session._id })).then((result) => {
          if (result.meta.requestStatus === "fulfilled") {
            setNursesBySession((prev) => ({ ...prev, [session._id]: { loading: false, list: result.payload.nurses } }));
            const assigned = getSessionNurseOption(session);
            const assignedFromList = assigned ? result.payload.nurses.find((n) => getNurseValue(n) === assigned.value) : null;
            const assignedOption = toNurseOption(assignedFromList) || assigned;
            if (assignedOption) {
              setAssignedNurses((prev) => ({ ...prev, [session._id]: assignedOption }));
            }
          } else {
            setNursesBySession((prev) => ({ ...prev, [session._id]: { loading: false, list: [] } }));
          }
        });
      }
    });
  }, [requestSessions, dispatch]);

  const handleBack = () => {
    nav(`${strings.navigation.manageNurseReq}?activeList=${backTab}`);
  };

  const handleOpenModal = (sessionId) => {
    const session = requestSessions.find((item) => item._id === sessionId);
    setModalSessionId(sessionId);
    setPendingNurse(assignedNurses[sessionId] || getSessionNurseOption(session) || null);
    setShowAssignModal(true);
  };

  const handleConfirmAssign = () => {
    if (!pendingNurse || !modalSessionId) {
      handleCancelModal();
      return;
    }

    const sessionIdToAssign = modalSessionId;
    const nurseToAssign = pendingNurse;
    setShowAssignModal(false);
    setModalSessionId(null);
    setPendingNurse(null);

    dispatch(assignSessionNurse({ sessionId: sessionIdToAssign, nurseId: nurseToAssign.value, nurse: nurseToAssign })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        setAssignedNurses((prev) => ({ ...prev, [sessionIdToAssign]: nurseToAssign }));
        ToastPopup.success(t("assignedSuccess"));
        dispatch(getRequestSessions({ requestId }));
      } else {
        ToastPopup.error(result.error?.message || t("fillAllFields"));
      }
    });
  };

  const handleSaveAssignments = () => {
    const assignmentsToSave = Object.entries(assignedNurses).filter(([sessionId, nurse]) => {
      const session = requestSessions.find((item) => item._id === sessionId);
      const apiAssigned = getSessionNurseOption(session);
      return nurse?.value && nurse.value !== apiAssigned?.value;
    });

    if (!assignmentsToSave.length) {
      nav(`${strings.navigation.manageNurseReq}?activeList=approved`);
      return;
    }

    Promise.all(assignmentsToSave.map(([sessionId, nurse]) => dispatch(assignSessionNurse({ sessionId, nurseId: nurse.value, nurse })))).then((results) => {
      const failed = results.find((result) => result.meta.requestStatus !== "fulfilled");
      if (failed) {
        ToastPopup.error(failed.error?.message || t("fillAllFields"));
        return;
      }
      ToastPopup.success(t("assignedSuccess"));
      nav(`${strings.navigation.manageNurseReq}?activeList=approved`);
    });
  };

  const handleCancelModal = () => {
    setShowAssignModal(false);
    setModalSessionId(null);
    setPendingNurse(null);
  };

  const modalNurseOptions = modalSessionId
    ? (nursesBySession[modalSessionId]?.list || []).map((n) => ({
        value: n._id,
        label: `${resolveLocalized(n.firstName)} ${resolveLocalized(n.lastName)}`.trim() || n.name || n._id,
      }))
    : [];

  const renderAssignNurseView = () => (
    <>
      <div className="whp-container assign-nurse-card">
        <div className="assign-nurse-header">
          <div className="title-hr2">
            <p>{t("assignNurse")}</p>
            <hr />
          </div>
          <button className="view-request-details-btn" onClick={() => setViewMode("fullDetails")}>
            {t("viewRequestDetails")}
          </button>
        </div>

        <div className="assign-nurse-table-wrap">
          <table className="ppl-table">
            <thead className="ppl-thead">
              <tr>
                <th>{t("patientDetails")}</th>
                <th>{t("serviceItem")}</th>
                <th>{t("sessionDate")}</th>
                <th>{t("status")}</th>
              </tr>
            </thead>
            <tbody className="ppl-tbody">
              {!requestDetails && (
                <tr>
                  <td colSpan={4} className="session-empty-cell">
                    {t("loading")}…
                  </td>
                </tr>
              )}
              {requestDetails && !requestDetails.services?.length && (
                <tr>
                  <td colSpan={4} className="session-empty-cell">
                    {t("noRecordFound")}
                  </td>
                </tr>
              )}
              {(requestDetails?.services || []).map((svc, idx) => {
                const patient = requestDetails.patient;
                const phone = patient?.countryCode ? `+${patient.countryCode} ${patient.phoneNumber}` : patient?.phoneNumber || "—";
                const serviceItemName = svc.serviceItem?.title?.en || svc.serviceItem?.title?.vi || "—";
                const sessionDate = requestDetails.startDate ? `${formatDate(requestDetails.startDate)}${requestDetails.endDate ? ` – ${formatDate(requestDetails.endDate)}` : ""}` : "—";
                const reqStatus = requestDetails.status || "VERIFIED";

                return (
                  <tr key={svc.serviceItem?._id || idx} className="ppl-table-row">
                    {/* Patient Details */}
                    <td>
                      <div className="ppl-td-first">
                        {patient?.profilePhoto ? (
                          <img src={patient.profilePhoto} alt="profile" className="ppl-td-profile" />
                        ) : (
                          <img src={Icons.profile} alt="profile" className="ppl-td-profile-none" />
                        )}
                        <div className="ppl-name-email">
                          <section className="session-patient-name">{patient?.fullName || "—"}</section>
                          <section className="ppl-email">{phone}</section>
                        </div>
                      </div>
                    </td>

                    <td>{serviceItemName}</td>
                    <td>{sessionDate}</td>

                    {/* Status from request */}
                    {(() => {
                      const displayStatus = reqStatus;
                      return (
                        <td className={`ppl-status ${displayStatus}`}>
                          <p>
                            {displayStatus === "ASSIGNED" && <img src={Icons.profile} alt="" className="status-icon-sm" />}
                            {displayStatus === "CONFIRMED" && <img src={Icons.tick} alt="" className="status-icon-sm" />}
                            {t(displayStatus) || displayStatus}
                          </p>
                        </td>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={handleBack}>
          {t("cancel")}
        </button>
        <button className="form-section-create" onClick={handleSaveAssignments}>
          {t("save")}
        </button>
      </div>

      {showAssignModal && (
        <div className="assign-nurse-modal-overlay" onClick={handleCancelModal}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{t("assignNurse")}</h3>
            <div className="assign-modal-body">
              <span className="assign-modal-label">{t("selectNurse") || "Assign Homecare Staff"}</span>
              <div className="assign-modal-dropdown">
                {nursesBySession[modalSessionId]?.loading ? (
                  <span className="assign-modal-loading">{t("loading")}…</span>
                ) : modalNurseOptions.length === 0 ? (
                  <span className="assign-modal-loading">{t("notAvailable")}</span>
                ) : (
                  <Widgets.NewSelectDropdown
                    options={modalNurseOptions}
                    selectedValue={pendingNurse}
                    onChange={setPendingNurse}
                    onEnterWhenClosed={pendingNurse ? handleConfirmAssign : undefined}
                    placeholder={t("notAssigned")}
                    noMargin
                  />
                )}
              </div>
            </div>
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={handleCancelModal}>
                {t("cancel")}
              </button>
              <button className="assign-modal-confirm" onClick={handleConfirmAssign}>
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderFullDetailsView = () => (
    <>
      {/* ── Patient Information Card ── */}
      <div className="whp-container ard-patient-card">
        <p className="ard-section-title">{t("patientInformation")}</p>
        <div className="ard-patient-body">
          {/* Left: photo + HN */}
          <div className="ard-patient-left">
            <img src={requestData.profilePhoto || Icons.noProfile} alt="profile" className="ard-profile-img" />
            <div className="ard-hn-block">
              <p className="ard-hn-label">
                {t("hnNumber")}<span className="asterik">*</span>
              </p>
              <div className="ard-hn-value">{requestData.hn}</div>
            </div>
          </div>

          {/* Right: details */}
          <div className="ard-patient-right">
            <p className="ard-patient-name">{requestData.patientName}</p>

            <div className="ard-info-row">
              <div className="ard-info-item">
                <img src={Icons.gender} alt="gender" />
                <span>{requestData.gender ? t(requestData.gender) : "-"}</span>
              </div>
              <div className="ard-info-item">
                <img src={Icons.CalenderIcon} alt="dob" />
                <span>{formatDate(requestData.dob)}</span>
              </div>
            </div>

            <div className="ard-info-row">
              <div className="ard-info-item">
                <img src={Icons.Call} alt="phone" />
                <span>{requestData.phone}</span>
              </div>
              <div className="ard-info-item">
                <img src={Icons.SmsIcon} alt="email" />
                <span>{requestData.email}</span>
              </div>
            </div>

            <div className="ard-info-item ard-insurance-row">
              <img src={Icons.insurance} alt="insurance" />
              <span>{requestData.insuranceCategory ? t(requestData.insuranceCategory) : "-"}</span>
            </div>

            {/* Patient Condition + Distance by KM — side by side */}
            <div className="ard-condition-zone-row">
              <div className="ard-condition-col">
                <p className="ard-field-label">{t("patientCondition")}</p>
                <div className="ard-readonly-field">{requestData.patientCondition}</div>
              </div>
              <div className="ard-zone-col">
                <p className="ard-field-label">
                  {t("noofkm") || "DistanKM"}
                  <span className="asterik">*</span>
                </p>
                <div className="ard-zone-value">{requestData.zone}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Patient Address — full width below */}
        <div className="ard-address-full-row">
          <div className="ard-info-item ard-address-head">
            <img src={Icons.Location} alt="location" />
            <span className="ard-field-label-green">{t("patientAddress")}</span>
          </div>
          <p className="ard-address-text">{requestData.address}</p>
        </div>
      </div>

      {/* ── Attachments ── */}
      {requestData.attachments.length > 0 && (
        <div className="whp-container ard-attachments-card">
          <p className="ard-section-title">{t("attachments")}</p>
          <div className="attachments-grid-readonly">
            {requestData.attachments.map((att, i) => (
              <div key={i} className="attachment-item-readonly">
                <div className="attachment-preview-readonly" onClick={() => openSecureFileUrl(att.url)}>
                  {att.type !== "image" ? (
                    <img src={Icons.PdfRedIcon} className="pdf-icon" alt="PDF" />
                  ) : (
                    <img
                      src={att.url}
                      className="attachment-image-fit"
                      alt={att.name}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.parentElement?.querySelector(".attachment-preview-fallback");
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  )}
                  <div className="attachment-preview-fallback">Preview unavailable</div>
                </div>
                <span className="attachment-name">{att.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Description ── */}
      {requestData.description && (
        <div className="ard-description-section">
          <p className="ard-section-title">{t("description")}</p>
          <div className="ard-description-text">
            <p>{requestData.description}</p>
          </div>
        </div>
      )}

      {/* ── Nurse Preference ── */}
      <div className="ard-pref-section">
        <p className="ard-section-title">{t("nursePreference")}</p>
        <div className="ard-pref-chips">
          <div className="ard-pref-chip">
            <img src={getLanguageIcon(requestData.nursePreferences.languages)} alt="lang" className="chip-flag" />
            <div>
              <p className="ard-chip-label">{t("preferredLanguage")}</p>
              <p className="ard-chip-value">{t(requestData.nursePreferences.languages.join(", "))}</p>
            </div>
          </div>
          <div className="ard-pref-chip">
            <img src={Icons.gender} alt="gender" className="chip-icon" />
            <div>
              <p className="ard-chip-label">{t("preferredGender")}</p>
              <p className="ard-chip-value">{t(requestData.nursePreferences.gender)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Quotation ── */}
      {(requestData.quotation.length > 0 || requestData.quotationAmount) && (
        <div className="ard-quotation-section">
          <p className="ard-section-title">{t("quotation")}</p>
          {requestData.quotationAmount && (
            <div className="ard-quotation-amount-row">
              <span>{t("quotationAmount")}</span>
              <strong>{requestData.quotationCurrencySymbol}{requestData.quotationAmount}</strong>
            </div>
          )}
          <div className="ard-quotation-list">
            {requestData.quotation.map((file, i) => (
              <div key={i} className="quotation-pdf-display" onClick={() => openSecureFileUrl(file.url)}>
                <img src={Icons.PdfRedIcon} className="pdf-icon" alt="PDF" />
                <span className="attachment-name">{file.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* ── Quotation ── */}
    </>
  );

  return (
    <div className="approved-details-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="approved-header-row">
        <div className="department-heading">{t("serviceDetails")}</div>
        <span className="confirmed-badge">{t(requestData.status) || requestData.status}</span>
      </div>

      <div className="approved-request-id">
        {t("serviceId")}: {requestSessions[0]?.nursingRequest?.nursingRequestID || requestData.id || requestId}
      </div>

      {/* Collect Cash / Waive Off — shown for VERIFIED (approved) requests */}
      {(requestData.status === "VERIFIED" || requestData.status === "ASSIGNED") && (
        <div className="ard-payment-actions-row">
          <button
            className="ard-collect-cash-btn"
            onClick={() => openPaymentModal("CASH")}
            disabled={paymentLoading["CASH"]}
          >
            {paymentLoading["CASH"] ? t("processing") : t("collectCash")}
          </button>
          <button
            className="ard-waive-off-btn"
            onClick={() => openPaymentModal("WAIVE_OFF")}
            disabled={paymentLoading["WAIVE_OFF"]}
          >
            {paymentLoading["WAIVE_OFF"] ? t("processing") : t("waiveOff")}
          </button>
        </div>
      )}

      {viewMode === "assignNurse" ? renderAssignNurseView() : renderFullDetailsView()}

      <Widgets.PaymentReasonModal
        open={!!paymentModal}
        type={paymentModal}
        loading={!!paymentLoading[paymentModal]}
        onCancel={handleClosePaymentModal}
        onSubmit={handlePaymentSubmit}
      />
    </div>
  );
};

export default ApprovedRequestDetails;
